/* eslint-disable no-nested-ternary */
import React, { useCallback, useEffect, useRef, useState } from "react";

import { Button, Icon, ModalDialog, PlainButton, TextField } from "@culturehq/components";
import client from "@culturehq/client";

import { useProfile } from "app/context";
import { useFieldsLabels } from "app/hooks";
import { getImage, getSearchValue, t, Trans } from "app/utils";
import { downloadPhoto, getUnsplashRandomImage } from "app/utils/unsplash";

import LazyEditor from "components/editor/LazyEditor";
import FindImageOnUnsplash from "components/FindImageOnUnsplash";
import imagesSizes from "constants/imagesSizes";
import SelectTags from "components/post/SelectTags";
import UsersModal from "components/UsersModal";
import tagsTypes from "constants/tagsTypes";
import CreateUserModal from "components/common/CreateUserModal";
import ChqModalDialog from "components/common/ChqModalDialog";
import hasCustomImageLibrary from "app/utils/hasCustomImageLibrary";
import CustomImageLibrary from "./post-stories-v2/common/PostStoryForm/custom-image-library/CustomImageLibrary";
import getCustomImageLibrary from "./post-stories-v2/common/PostStoryForm/custom-image-library/getCustomImageLibrary";

const getEntityParams = values => {
  const params = {
    departmentIds: [],
    interestIds: [],
    locationIds: [],
    organizationIds: [],
    skillIds: [],
    userIds: [],
    classYearIds: [],
    clubIds: [],
    programIds: [],
    degreeIds: [],
    companyIds: []
  };

  values.forEach(value => {
    if (!value.value) return;

    switch (value.category) {
      case "departments":
        params.departmentIds.push(value.value);
        break;
      case "interests":
        params.interestIds.push(value.value);
        break;
      case "locations":
        params.locationIds.push(value.value);
        break;
      case "skills":
        params.skillIds.push(value.value);
        break;
      case "classYears":
      case "class years":
        params.classYearIds.push(value.value);
        break;
      case "clubs":
        params.clubIds.push(value.value);
        break;
      case "programs":
        params.programIds.push(value.value);
        break;
      case "degrees":
        params.degreeIds.push(value.value);
        break;
      case "companies":
        params.companyIds.push(value.value);
        break;
      case tagsTypes.ALL_ORGANIZATION:
        params.organizationIds.push(value.value);
        break;
      default:
        params.userIds.push(value.value);
        break;
    }
  });

  return params;
};

const StoryShareUsers = ({
  children, storyQuestion, imageToShow = undefined, onClose: onCloseMenu
}) => {
  const [changed, setChanged] = useState(false);
  const editorRef = useRef(null);
  const labels = useFieldsLabels();
  const { profile } = useProfile();
  const [open, setOpen] = useState(false);
  const [openInviteUsersModal, setOpenInviteUsersModal] = useState(false);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showUnsplashSearch, setShowUnsplashSearch] = useState(false);
  const [selectedImage, setSelectedImage] = useState(imageToShow);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [newUsers, setNewUsers] = useState([]);
  const [usersPagination, setPagination] = useState(undefined);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchValue] = useState(getSearchValue(storyQuestion));
  const orgName = profile.organization.name;
  const hasLibrary = hasCustomImageLibrary(orgName);

  const onChange = useCallback(() => {
    setChanged(true);
  }, [setChanged]);

  const onConfirmClose = useCallback(
    () => {
      const message = t("You have unsaved changes, are you sure you'd like to close the send prompt modal?");

      // eslint-disable-next-line no-alert
      if (!changed || window.confirm(message)) onClose();
    },
    [changed, onClose]
  );

  useEffect(
    () => {
      const isTrend = storyQuestion?.childStoryQuestions?.length > 0;
      let isMounted = true;

      const getRandomImage = async tag => {
        if (hasLibrary) {
          const images = getCustomImageLibrary(orgName);
          const max = images.length - 1;
          const randomNum = Math.floor(Math.random() * (max)) + 1;
          const result = images[randomNum];
          if (isMounted) setSelectedImage(result);
        } else {
          const result = await getUnsplashRandomImage(tag);
          if (isMounted) setSelectedImage(result);
        }
      };

      if (!selectedImage && !isTrend) {
        getRandomImage(searchValue);
      } else {
        setSelectedImage(storyQuestion.imageUrl);
      }

      return () => {
        isMounted = false;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchValue]
  );

  const onReset = useCallback(
    () => {
      setNote("");
      setSubmitting(false);
      setSelectedOptions([]);
      setUsers([]);
      setPagination(undefined);
      setChanged(false);
    },
    []
  );

  const onOpen = useCallback(() => setOpen(true), [setOpen]);
  const onClose = useCallback(
    () => {
      setOpen(false);
      if (onCloseMenu) onCloseMenu();
      onReset();
    },
    [onCloseMenu, onReset, setOpen]
  );

  const getResponse = useCallback(
    async (storyQuestionId, imageUrl, images, imageAlt) => {
      await client.shareStoryQuestion({
        storyQuestionId,
        imageUrl,
        selectedOptions: selectedOptions.map(option => {
          if (!option.email) {
            return option;
          }
          return { ...option, label: option.label.replace(" (New user)", "") };
        }),
        slackChannel: undefined,
        note,
        shareInPlatform: true,
        thumbUrl: images.thumbUrl,
        fullUrl: images.fullUrl,
        smallUrl: images.smallUrl,
        imageAlt
      });
      onClose();
    },
    [onClose, selectedOptions, note]
  );

  const onAWSUploadedFile = useCallback(
    async (storyQuestionId, imageUrl, images, imageAlt) => {
      await getResponse(storyQuestionId, imageUrl, images, imageAlt);
    },
    [getResponse]
  );

  const waitForS3 = useCallback(
    async (ms, storyQuestionId, imageUrl, images, imageAlt) => (
      new Promise(_ => {
        // This timeout is to allow AWS to upload the file, in this point,
        // AWs has responded with a 200 status, but we need to take care of
        // S3 Consistency Model
        setTimeout(onAWSUploadedFile, ms, storyQuestionId, imageUrl, images, imageAlt);
      })
    ),
    [onAWSUploadedFile]
  );

  const onClick = useCallback(
    async () => {
      setSubmitting(true);
      let imageUrl = null;
      let images = {
        thumbUrl: "",
        fullUrl: "",
        smallUrl: ""
      };
      let imageAlt = null;
      if (selectedImage && typeof selectedImage === "string" && selectedImage.startsWith("blob:")) {
        if (editorRef.current) {
          const body = await editorRef.current.serialize();
          await waitForS3(
            2000, storyQuestion.id, JSON.parse(body).entityMap[0].data.src, images, imageAlt
          );
        }
      } else {
        const { urls } = selectedImage;
        if (urls) {
          imageUrl = urls.regular;
          images = {
            thumbUrl: urls.thumb,
            fullUrl: urls.full,
            smallUrl: urls.small
          };
          imageAlt = selectedImage.alt_description;

          downloadPhoto(selectedImage);
        } else {
          // this case is just with a default image (no more unsplash images and no local image)
          imageUrl = selectedImage;
        }
        await waitForS3(2000, storyQuestion?.id, imageUrl, images, imageAlt);
      }
    },
    [selectedImage, storyQuestion, waitForS3]
  );

  const handleSelectImage = image => {
    setSelectedImage(image);
    setShowUnsplashSearch(false);
    onChange();
  };

  const handleFindOnUnsplash = value => {
    setShowUnsplashSearch(value);
  };

  const onImageSelected = editorOutput => {
    const { entityMap } = JSON.parse(editorOutput);
    const imagesKeys = Object.keys(entityMap);
    setSelectedImage(entityMap[imagesKeys.length - 1].data.src);
    onChange();
  };

  const fetchUsers = async page => {
    setLoading(true);
    const { pagination, users: newUsers } = await client.listEntityUsers(
      { ...getEntityParams(selectedOptions), page }
    );
    setUsers(newUsers);
    setPagination(pagination);
    setLoading(false);
  };

  const handleShareWithChanged = async (newOptions, _) => {
    if (typeof newOptions.at(-1) === "string" || newOptions.at(-1) instanceof String) {
      setOpenInviteUsersModal(newOptions.at(-1));
    }
    setSelectedOptions(newOptions);
    setLoading(true);
    const { pagination, users: newUsers } = await client.listEntityUsers(
      { ...getEntityParams(newOptions) }
    );
    setUsers(newUsers);
    setPagination(pagination.totalCount === 0 ? undefined : pagination);
    setLoading(false);
    onChange();
  };

  const onNoteChanged = value => {
    setNote(value);
    onChange();
  };

  const handleUserModalClose = () => {
    const options = selectedOptions;
    options.splice(-1, 1);
    setSelectedOptions(options);
    setOpenInviteUsersModal();
  };

  const handleUserSuccess = (name, email, firstName, lastName) => {
    let options = selectedOptions;
    options.splice(-1, 1);
    const newUser = {
      category: tagsTypes.USERS,
      categoryIcon: "user",
      icon: "user-filled",
      label: `${name} (New user)`,
      value: email,
      email,
      firstName,
      lastName
    };
    options = [...options, newUser];
    setNewUsers([...newUsers, newUser]);
    setSelectedOptions(options);
    setOpenInviteUsersModal();
  };

  return (
    <>
      {children(onOpen)}
      {open && (
        <ChqModalDialog className="Story-Share-Modal" onClose={onConfirmClose}>
          <ModalDialog.Heading onClose={onConfirmClose}>
            <Trans>Send to specific users to get people&apos;s stories</Trans>
          </ModalDialog.Heading>
          <ModalDialog.Body>
            {openInviteUsersModal && (
              <CreateUserModal
                initialOpen
                name={openInviteUsersModal}
                onCancel={handleUserModalClose}
                onSuccess={handleUserSuccess}
                isTrend={!storyQuestion.parentStoryQuestion}
              />
            )}
            <div className="gallery-lightbox__title-container">
              <Icon icon="quote" />
              <h1 className="title">{storyQuestion.question}</h1>
            </div>
            <hr />
            <div className="Select-Image">
              <p className="Story-Share-Modal__label">
                <Trans>Add an image or GIF</Trans>
              </p>
              {selectedImage && (
                <div className="image-container">
                  <div className="gallery-lightbox__image-container" style={{ backgroundImage: `url(${getImage(imagesSizes.SMALL, selectedImage, hasLibrary)})` }} />
                  <img className="image" src={getImage(imagesSizes.SMALL, selectedImage, hasLibrary)} alt="Selected" />
                </div>
              )}
              <LazyEditor
                sections={["image"]}
                forzeOpen
                ref={editorRef}
                onChange={onImageSelected}
              />
              <PlainButton className="unsplash-btn" onClick={() => { setShowUnsplashSearch(!showUnsplashSearch); }}>
                <Icon icon="search" /><Trans>{ hasLibrary ? "Find Images" : "Search Unsplash"}</Trans>
              </PlainButton>
              {showUnsplashSearch && (
                <div style={{ position: "relative" }}>
                  {hasLibrary ? (
                    <CustomImageLibrary
                      inline
                      organizationName={orgName}
                      onSelectImage={handleSelectImage}
                    />
                  )
                    : (
                      <FindImageOnUnsplash
                        inline
                        onSelectImage={handleSelectImage}
                        search={searchValue}
                        showSearch={handleFindOnUnsplash}
                      />
                    )}
                </div>
              )}
            </div>
            <TextField className="note-field" onChange={onNoteChanged} value={note} placeholder={t("Ex. I thought you’d find this story prompt both interesting and relevant! I’d love to hear your story.")}>
              <h4><Trans>Add a Note</Trans></h4>
            </TextField>
            <div className="tags-container">
              <h4>
                <Trans>
                  Select Users
                  {" "}
                  <span className="legend">{t(`people, interests, ${labels.departments.plural.toLowerCase()}, etc.`)}</span>
                </Trans>
              </h4>
              <SelectTags
                creatable={profile.organizationAdmin || profile.role === "content_admin"}
                creatableLabel={t("Create user")}
                normalDesign={false}
                onShareWithChanged={handleShareWithChanged}
                placeholder={t("Select users")}
                selectedOptions={selectedOptions}
                withUsers
                wholeCompany
                newOptions={newUsers}
              />
              <p className="Story-Share-Modal__explanation"><Trans>* Sending an in-app notification and email to these users</Trans></p>
            </div>
            {(usersPagination?.totalCount || newUsers.length > 0) && (
              <UsersModal
                usersPagination={usersPagination}
                loading={loading}
                selectedOptions={selectedOptions}
                users={users}
                fetchUsers={fetchUsers}
              />
            )}
          </ModalDialog.Body>
          <ModalDialog.Footer>
            <div>
              <Button primary onClick={onClick} disabled={submitting}>
                {submitting ? <Trans>Sharing...</Trans> : <Trans>Send to Users</Trans>}
              </Button>
            </div>
          </ModalDialog.Footer>
        </ChqModalDialog>
      )}
    </>
  );
};

export default StoryShareUsers;
